import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import PageHeader from '@/components/shared/PageHeader';
import { SEOHead } from '@/components/shared/SEOHead';
import { newsArticlesList, newsCategories, mockRSSItems, rssFeedSources } from '@/data/newsData';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight, ExternalLink, Rss } from 'lucide-react';
import { useRssFeeds } from '@/hooks/useRssFeeds';

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const NewsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [filteredArticles, setFilteredArticles] = useState(newsArticlesList);
  const { items: rssItems, loading: rssLoading, error: rssError } = useRssFeeds(rssFeedSources, { limit: 8 });

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredArticles(newsArticlesList);
    } else {
      setFilteredArticles(
        newsArticlesList.filter(article => article.category === selectedCategory)
      );
    }
  }, [selectedCategory]);

  const featuredArticle = newsArticlesList[0];

  const fallbackIndustry = mockRSSItems.map((item) => ({
    id: item.id,
    title: item.title,
    excerpt: item.excerpt,
    date: item.date,
    source: item.source,
    link: item.url,
  }));

  const mapIndustryItems = (items = []) =>
    items.map((item) => ({
      id: item.id,
      title: item.title,
      excerpt: item.excerpt,
      date: item.date,
      source: item.source,
      link: item.link,
    }));

  const industryItems = mapIndustryItems(rssItems.length ? rssItems : fallbackIndustry);

  return (
    <Layout>
      <SEOHead
        title="News & Industry Updates"
        description="Stay informed with Redspire Civil Engineering's latest news, project updates, and industry insights in infrastructure development and construction."
        keywords={['civil engineering news', 'infrastructure updates', 'construction news', 'engineering articles']}
      />
      
      <PageHeader
        title="News & Updates"
        subtitle="Stay informed about our latest projects, achievements, and industry developments."
        breadcrumb="News"
        backgroundImage="https://images.unsplash.com/photo-1536895058696-a69b1c7ba34f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxjaXZpbCUyMGVuZ2luZWVyaW5nfGVufDB8fHx8MTc2NDE0MzU2OHww&ixlib=rb-4.1.0&q=85"
      />

      {/* Featured Article */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Featured Article</h2>
          <Link to={featuredArticle.href}>
            <Card className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-shadow duration-300">
              <div className="grid lg:grid-cols-2">
                <div className="aspect-[4/3] lg:aspect-auto overflow-hidden">
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <CardContent className="p-8 lg:p-12 flex flex-col justify-center">
                  <Badge className="bg-red-600 hover:bg-red-700 w-fit mb-4">
                    Featured
                  </Badge>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(featuredArticle.date)}
                    </span>
                    <Badge variant="outline">{featuredArticle.category}</Badge>
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {featuredArticle.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-2 text-red-600 font-semibold hover:gap-4 transition-all">
                    Read Full Article
                    <ArrowRight className="w-5 h-5" />
                  </span>
                </CardContent>
              </div>
            </Card>
          </Link>
        </div>
      </section>

      {/* Category Filter & Internal Articles */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8">
            <h2 className="text-3xl font-bold text-gray-900">Our Articles</h2>
            
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {newsCategories.map((category) => (
                <Button
                  key={category}
                  variant={selectedCategory === category ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(category)}
                  className={
                    selectedCategory === category
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'hover:bg-red-50 hover:text-red-600 hover:border-red-600'
                  }
                >
                  {category}
                </Button>
              ))}
            </div>
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Link key={article.id} to={article.href}>
                  <Card
                    className="group overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300 bg-white h-full"
                  >
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-red-600 hover:bg-red-700">
                        {article.category}
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(article.date)}</span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2 group-hover:text-red-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      {article.tags && article.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {article.tags.slice(0, 2).map((tag) => (
                            <Badge key={tag} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <div className="pt-4 border-t border-gray-100">
                        <span className="inline-flex items-center gap-2 text-red-600 font-medium text-sm group-hover:gap-3 transition-all">
                          Read More
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No articles found in this category.</p>
            </div>
          )}
        </div>
      </section>

      {/* External RSS Feed Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Rss className="w-8 h-8 text-red-600" />
            <h2 className="text-3xl font-bold text-gray-900">Industry News</h2>
          </div>
          
          <p className="text-gray-600 mb-8 max-w-3xl">
            Stay updated with the latest developments in civil engineering and infrastructure from leading industry sources including ENR, ASCE, Construction Dive, and Infrastructure Intelligence.
          </p>

          {rssLoading && (
            <p className="text-sm text-gray-500 mb-6">Loading live RSS feeds from industry publishers...</p>
          )}
          {rssError && (
            <p className="text-sm text-red-600 mb-6">
              Live feeds are temporarily unavailable. Showing curated highlights instead.
            </p>
          )}

          {/* RSS Feed Sources */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {rssFeedSources.map((source) => (
              <Card key={source.name} className="border border-gray-200 hover:border-red-600 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    <Rss className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-gray-900 text-sm mb-1">{source.name}</h4>
                      <p className="text-xs text-gray-600">{source.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* RSS Feed Items */}
          <div className="space-y-4">
            {industryItems.map((item) => (
              <a
                key={item.id}
                href={item.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card className="group border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gray-50 hover:bg-white">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge variant="outline" className="text-xs">
                            {item.source}
                          </Badge>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(item.date)}
                          </span>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-gray-600 text-sm">
                          {item.excerpt}
                        </p>
                      </div>
                      <ExternalLink className="w-5 h-5 text-gray-400 group-hover:text-red-600 transition-colors flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              External articles are provided from industry RSS feeds and open in new windows.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Want to Stay Updated?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
            Follow Redspire for the latest news on our projects and industry insights.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="gap-2">
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button size="lg" variant="outline" className="gap-2 bg-transparent text-white border-white hover:bg-white hover:text-red-600">
                View Our Projects
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsPage;
